import { useEffect, useRef, useState } from 'react';
import { Mic, Send, X, Trash2, Loader2, Play, Square, MessageCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { voiceOrders as voiceOrdersApi } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * "Order by Voice" — accessibility-first ordering for elderly / low-literacy
 * users. Records up to 60 s via the browser MediaRecorder API, uploads to
 * Emergent Object Store, and hands the customer a WhatsApp deep-link with a
 * pre-filled "Hi, I have a new voice order for you..." message.
 *
 * Props:
 *   store          : store object (needs .id, .name, .phone)
 *   size           : 'large' (Home) | 'inline' (StoreDetail card)
 */
export default function VoiceOrderButton({ store, size = 'inline' }) {
  const [open, setOpen] = useState(false);
  if (!store) return null;
  const label = size === 'large' ? 'Order by Voice' : 'Voice order';
  const cls = size === 'large'
    ? 'inline-flex items-center justify-center gap-2 rounded-lg bg-[#0D3F12] hover:bg-[#0A320E] text-white font-black uppercase tracking-wider text-base h-14 px-5 border-2 border-[#0D3F12] shadow-lg w-full'
    : 'inline-flex items-center justify-center gap-2 rounded-md bg-[#0D3F12] hover:bg-[#0A320E] text-white font-black uppercase tracking-wider text-xs h-10 px-4';
  return (
    <>
      <button
        data-testid={`voice-order-button-${size}`}
        onClick={() => setOpen(true)}
        className={cls}
        title={`Send a voice order to ${store.name}`}
      >
        <Mic className={size === 'large' ? 'w-6 h-6' : 'w-4 h-4'}/>
        {label}
      </button>
      {open && <VoiceOrderDialog store={store} onClose={() => setOpen(false)}/>}
    </>
  );
}

const MAX_SECONDS = 60;

function VoiceOrderDialog({ store, onClose }) {
  const { user } = useAuth();

  const [phase, setPhase] = useState('idle');    // idle | recording | preview | uploading | sent
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);        // {audio_url, whatsapp_link, id, ...}

  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    if (mediaRecRef.current && mediaRecRef.current.state === 'recording') {
      try { mediaRecRef.current.stop(); } catch { /* ignore stop-on-cleanup errors */ }
    }
  }, [blobUrl]);

  const startRecording = async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Your browser does not support voice recording.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      // Prefer webm/opus, fallback to browser default
      let mime = '';
      if (typeof MediaRecorder !== 'undefined') {
        if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mime = 'audio/webm;codecs=opus';
        else if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm')) mime = 'audio/webm';
        else if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) mime = 'audio/ogg;codecs=opus';
      }
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const b = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        setPhase('preview');
      };
      rec.start(200);
      setPhase('recording');
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((s) => {
          const n = s + 1;
          if (n >= MAX_SECONDS) stopRecording();
          return n;
        });
      }, 1000);
    } catch (e) {
      setError(e?.message || 'Microphone access denied. Please allow it in your browser settings.');
      setPhase('idle');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const r = mediaRecRef.current;
    if (r && r.state === 'recording') { try { r.stop(); } catch { /* ignore */ } }
  };

  const discardAndClose = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null); setBlobUrl(null);
    setPhase('idle'); setElapsed(0);
    onClose();
  };

  const rerecord = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null); setBlobUrl(null);
    setElapsed(0); setPhase('idle');
  };

  const sendToShop = async () => {
    if (!user) { toast.error('Please sign in first'); return; }
    if (!blob) return;
    setPhase('uploading');
    try {
      const ext = (blob.type.includes('ogg') ? 'ogg' : blob.type.includes('mp4') ? 'm4a' : blob.type.includes('wav') ? 'wav' : 'webm');
      const fd = new FormData();
      fd.append('audio', new File([blob], `voice-order.${ext}`, { type: blob.type || 'audio/webm' }));
      fd.append('store_id', store.id);
      fd.append('duration_sec', String(elapsed));
      const r = await voiceOrdersApi.create(fd);
      setSent(r); setPhase('sent');
      if (r.whatsapp_link) {
        // Open WhatsApp in a new tab immediately so the merchant hears it right away.
        window.open(r.whatsapp_link, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Upload failed. Please try again.');
      setPhase('preview');
    }
  };

  const secs = String(elapsed).padStart(2, '0');

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={phase === 'recording' ? null : onClose}>
      <div
        data-testid="voice-order-dialog"
        className="bg-white w-full max-w-md rounded-2xl p-6 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {phase !== 'recording' && (
          <button onClick={onClose} className="absolute top-3 right-3 text-ink/40 hover:text-ink" aria-label="Close">
            <X className="w-5 h-5"/>
          </button>
        )}
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-black text-[#0D3F12]">
          <Mic className="w-4 h-4"/>Voice order
        </div>
        <h3 className="font-heading font-black text-2xl mt-1">Talk to {store.name}</h3>
        <p className="text-sm text-ink/70 mt-1">
          Record your order (up to 60 seconds) — we&apos;ll send it straight to the shop&apos;s WhatsApp.
        </p>

        {error && (
          <div data-testid="voice-order-error" className="mt-3 rounded-lg bg-[#FFEBEE] border border-[#FFCDD2] p-2.5 text-xs text-[#B71C1C]">
            {error}
          </div>
        )}

        {/* IDLE */}
        {phase === 'idle' && (
          <div className="mt-6 flex flex-col items-center">
            <button
              data-testid="voice-order-start-rec"
              onClick={startRecording}
              className="w-24 h-24 rounded-full bg-[#B71C1C] hover:bg-[#8B0000] text-white grid place-items-center shadow-lg ring-4 ring-[#B71C1C]/20 transition"
              aria-label="Start recording"
            >
              <Mic className="w-10 h-10"/>
            </button>
            <div className="mt-3 text-sm font-black uppercase tracking-wider text-ink">Tap to record</div>
            <div className="mt-1 text-xs text-ink/50">Max 60 seconds</div>
          </div>
        )}

        {/* RECORDING */}
        {phase === 'recording' && (
          <div data-testid="voice-order-recording" className="mt-6 flex flex-col items-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[#B71C1C]/25 animate-ping"/>
              <div className="relative w-24 h-24 rounded-full bg-[#B71C1C] text-white grid place-items-center shadow-xl ring-8 ring-[#B71C1C]/20">
                <Mic className="w-10 h-10"/>
              </div>
            </div>
            <div className="mt-4 text-2xl font-black font-mono text-[#B71C1C]">0:{secs} / 1:00</div>
            <div className="mt-1 text-xs uppercase tracking-wider font-black text-[#B71C1C]">Recording…</div>
            <button
              data-testid="voice-order-stop-rec"
              onClick={stopRecording}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-ink text-white font-black uppercase text-sm tracking-wider px-6 h-12"
            >
              <Square className="w-4 h-4"/>Stop recording
            </button>
          </div>
        )}

        {/* PREVIEW */}
        {phase === 'preview' && blobUrl && (
          <div data-testid="voice-order-preview" className="mt-6">
            <div className="rounded-xl bg-muted p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0D3F12] text-white grid place-items-center shrink-0">
                <Play className="w-5 h-5"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink/60">Your voice message</div>
                <div className="text-sm font-bold text-ink">{elapsed}s recorded</div>
              </div>
            </div>
            <audio data-testid="voice-order-audio" src={blobUrl} controls className="w-full mt-3"/>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                data-testid="voice-order-cancel"
                onClick={rerecord}
                className="inline-flex items-center justify-center gap-1 rounded-lg border-2 border-ink text-ink font-black uppercase text-xs tracking-wider h-11"
              >
                <Trash2 className="w-4 h-4"/>Re-record
              </button>
              <button
                data-testid="voice-order-send"
                onClick={sendToShop}
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#0D3F12] hover:bg-[#0A320E] text-white font-black uppercase text-xs tracking-wider h-11"
              >
                <Send className="w-4 h-4"/>Send to shop
              </button>
            </div>
          </div>
        )}

        {/* UPLOADING */}
        {phase === 'uploading' && (
          <div className="mt-8 flex flex-col items-center text-ink/70">
            <Loader2 className="w-10 h-10 animate-spin text-[#0D3F12]"/>
            <div className="mt-3 text-sm font-black uppercase tracking-wider">Sending to shop…</div>
          </div>
        )}

        {/* SENT */}
        {phase === 'sent' && sent && (
          <div data-testid="voice-order-sent" className="mt-6">
            <div className="rounded-lg bg-[#E8F5E9] border-2 border-[#1B5E20] p-4">
              <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-black text-[#1B5E20]">
                <CheckCircle2 className="w-4 h-4"/>Sent
              </div>
              <div className="mt-1 font-heading font-black text-lg text-[#1B5E20]">
                Voice message uploaded ✓
              </div>
              <p className="text-xs text-ink/70 mt-1">
                We&apos;ve opened WhatsApp with a pre-filled message for the shopkeeper. If nothing opened, tap the button below.
              </p>
            </div>
            <div className="mt-4 space-y-2">
              {sent.whatsapp_link ? (
                <a
                  data-testid="voice-order-open-whatsapp"
                  href={sent.whatsapp_link}
                  target="_blank" rel="noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white font-black uppercase text-sm tracking-wider h-12"
                >
                  <MessageCircle className="w-5 h-5"/>Open WhatsApp
                </a>
              ) : (
                <div className="rounded-lg bg-[#FFF3E0] border border-[#FFCC80] p-2.5 text-xs text-[#8D3A0A]">
                  This shop doesn&apos;t have WhatsApp yet — but they&apos;ll still see your voice order in their Merchant Dashboard.
                </div>
              )}
              <button
                data-testid="voice-order-done"
                onClick={discardAndClose}
                className="w-full inline-flex items-center justify-center rounded-lg border-2 border-ink text-ink font-black uppercase text-xs tracking-wider h-11"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
